import type { ApiClient } from '@klareospace/pharos-lib-front-utils';
import type {
  CreateVehicleTemplate,
  PaginatedResponse,
  PharosApiResponse,
  PharosPaginatedResponse,
  UpdateVehicleTemplate,
  VehicleTemplate,
} from '@klareospace/pharos-lib-types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SearchVehicleTemplatesParams {
  limit?: number;
  page?: number;
  profiles?: string[];
  search?: string;
  statuses?: string[];
}

// ─── API factory ──────────────────────────────────────────────────────────────

export function createVehicleTemplateApi(client: ApiClient): {
  archiveVehicleTemplate: (id: string) => Promise<VehicleTemplate>;
  createVehicleTemplate: (data: CreateVehicleTemplate) => Promise<VehicleTemplate>;
  searchVehicleTemplates: (params?: SearchVehicleTemplatesParams) => Promise<PaginatedResponse<VehicleTemplate>>;
  updateVehicleTemplate: (id: string, data: UpdateVehicleTemplate) => Promise<VehicleTemplate>;
} {
  return {
    archiveVehicleTemplate: async (id: string): Promise<VehicleTemplate> => {
      const response = await client.execute<PharosApiResponse<VehicleTemplate>>(() =>
        client.instance.delete(`/vehicle-templates/${id}`),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    createVehicleTemplate: async (data: CreateVehicleTemplate): Promise<VehicleTemplate> => {
      const response = await client.execute<PharosApiResponse<VehicleTemplate>>(() =>
        client.instance.post('/vehicle-templates', data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    searchVehicleTemplates: async (
      params?: SearchVehicleTemplatesParams,
    ): Promise<PaginatedResponse<VehicleTemplate>> => {
      const body = toSearchBody(params);
      const query: Record<string, number | undefined> = {
        limit: params?.limit,
        page: params?.page,
      };
      const response = await client.execute<PharosApiResponse<PharosPaginatedResponse<VehicleTemplate>>>(() =>
        client.instance.post('/vehicle-templates/search', body, { params: query }),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },

    updateVehicleTemplate: async (id: string, data: UpdateVehicleTemplate): Promise<VehicleTemplate> => {
      const response = await client.execute<PharosApiResponse<VehicleTemplate>>(() =>
        client.instance.patch(`/vehicle-templates/${id}`, data),
      );
      if (!response.data) {
        throw new Error('Unexpected empty response from API');
      }
      return response.data;
    },
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Build the POST body for /vehicle-templates/search from filter params.
 * Only includes fields that have been explicitly set.
 */
function toSearchBody(params?: SearchVehicleTemplatesParams): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  const trimmedSearch = params?.search?.trim();
  if (trimmedSearch) {
    body.search = trimmedSearch;
  }
  if (params?.profiles && params.profiles.length > 0) {
    body.profiles = params.profiles;
  }
  if (params?.statuses && params.statuses.length > 0) {
    body.statuses = params.statuses;
  }
  return body;
}
